<template>
  <div class="modal-container__wrapper">
    <button
      v-if="triggerElementType === 'button'"
      :class="triggerStyles"
      @click="openModal"
    >
      {{ triggerLabel }}
    </button>
    <a
      v-else-if="triggerElementType === 'link'"
      href="#"
      :class="triggerStyles"
      @click.prevent="openModal"
    >
      {{ triggerLabel }}
    </a>
    <div
      v-if="localShowModal"
      class="modal-container__blured-bg"
      @click="focusOutCloseAction"
    ></div>
    <transition :name="transitionName">
      <div
        v-if="localShowModal"
        :class="['modal-container', customTop && 'modal-container--custom']"
        @click="focusOutCloseAction"
      >
        <div
          class="modal-container__modal"
          :style="{ top: customTop ? customTop : 'unset' }"
          @click.stop
        >
          <div class="modal-container__container">
            <button
              v-if="showCloseBtnX"
              class="modal-container__close"
              @click="closeModal"
            >
              <XIcon class="modal-container__close--icon" />
            </button>
            <slot></slot>
            <button
              v-if="showCloseBtn"
              :class="['modal-container__close-btn', closeBtnStyles]"
              @click="closeModal"
            >
              {{ closeBtnLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { XIcon } from 'vue-feather-icons'

export default {
  name: 'ModalContainer',
  emits: ['open-modal', 'close-modal'],
  components: {
    XIcon,
  },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    showCloseBtnX: {
      type: Boolean,
      default: true,
    },
    showCloseBtn: {
      type: Boolean,
      default: true,
    },
    closeBtnLabel: {
      type: String,
      default: 'Close',
    },
    closeBtnStyles: {
      type: String,
      default: 'btn btn--primary btn--small',
    },
    focusOutClose: {
      type: Boolean,
      default: true,
    },
    triggerLabel: {
      type: String,
      default: 'Open Modal',
    },
    triggerStyles: {
      type: String,
      default: 'btn btn--primary btn--small',
    },
    buttonTrigger: {
      type: Boolean,
      default: false,
    },
    linkTrigger: {
      type: Boolean,
      default: false,
    },
    modalSlide: {
      type: Boolean,
      default: false,
    },
    modalSlideFade: {
      type: Boolean,
      default: false,
    },
    modalFade: {
      type: Boolean,
      default: false,
    },
    noTransition: {
      type: Boolean,
      default: false,
    },
    customTop: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      localShowModal: this.showModal || false,
    }
  },
  computed: {
    triggerElementType() {
      if (this.buttonTrigger) {
        return 'button'
      } else if (this.linkTrigger) {
        return 'link'
      }
      return 'button' // Default to button if no prop is present
    },
    transitionName() {
      if (this.noTransition) {
        return ''
      } else if (this.modalFade) {
        return 'modal-fade'
      } else if (this.modalSlideFade) {
        return 'modal-slide-fade'
      }
      return 'modal-slide'
    },
  },
  watch: {
    // This watcher is used to close the modal
    // when the prop showModal is set to false externaly from the component
    showModal: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
    },
  },
  methods: {
    openModal() {
      this.localShowModal = true
      this.$emit('open-modal')
    },
    closeModal() {
      this.localShowModal = false
      this.$emit('close-modal')
    },
    focusOutCloseAction() {
      if (this.focusOutClose) {
        this.closeModal()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* modal-fade */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.5s;
}
.modal-fade-enter,
.modal-fade-leave-to /* .modal-fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
/* END modal-fade */

/* modal-slide */
.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: all 0.5s ease;
}
.modal-slide-enter,
.modal-slide-leave-to /* .modal-slide-leave-active in <2.1.8 */ {
  transform: translateY(-100%);
  opacity: 0;
}
/* END modal-slide */

/* modal-slide-fade */
.modal-slide-fade-enter-active,
.modal-slide-fade-leave-active {
  transition: all 0.5s ease;
}
.modal-slide-fade-enter,
.modal-slide-fade-leave-to /* .modal-slide-fade-leave-active in <2.1.8 */ {
  transform: translateY(-100%);
  opacity: 0;
}
/* END modal-slide-fade */

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  &__blured-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(17.5px);
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &--custom {
    align-items: unset;
    .modal-container__modal {
      position: relative;
      height: fit-content;
    }
  }
  &__modal {
    overflow: hidden;
    background: var(--dark-brighter);
    border-radius: 24px;
  }
  &__container {
    position: relative;
    background: var(--glass-mid-gray);
    padding: 30px;
    min-height: 400px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: auto 0;
    overflow: auto;
    overflow-x: hidden;
    max-height: 90vh;
    &::-webkit-scrollbar {
      width: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--perfect-gray);
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--purple-pale);
    }

    &::-webkit-scrollbar-track {
      background-color: var(--glass-mid-gray);
    }
  }
  &__close {
    position: absolute;
    right: 0;
    top: 0;
    background: none;
    border: none;
    cursor: pointer;
    margin: 30px;
    &--icon {
      stroke: var(--white);
    }
  }
}
</style>
